import React, { useState } from 'react'
import { Button, Col, Form, FormInstance, Input, Radio, Row, Select, Switch, Upload } from 'antd'
import { EnvironmentOutlined } from '@ant-design/icons'
import { Image as AntImage } from 'antd'
import type { UploadProps } from 'antd'
import { leagueUpdatesStore } from 'state/league/leagueUpdates'
import { leagueEventStore } from 'state/league/leagueEvent'
import { myStore } from 'state/store'
import {
  useCreateLeagueUpdate,
  useEditLeagueUpdate,
} from 'api/league/leagueUpdates/leagueUpdates'
import { useGetLeaguePlayers } from 'api/league/LeagueManage/leagueManage'
import { useFileUploadRequest } from 'api/FileUpload'
import { LeagueUpdatePayloadType, LeagueUpdateType, LeagueUpdateVisibility } from 'types/league/leagueUpdates'
import MapModal from 'helper/Map/MapModal'
import ViewAddress from 'helper/Map/ViewAddress'
import Gallery from 'assets/Images/galleryIcon.svg'
import PlayerImage from 'assets/Images/playerIcon.svg'
import Music from 'assets/Images/musicIcon.svg'
import DocumentIcon from 'assets/Images/document.svg'
import Icon from 'assets/Images/icon.svg'

const { TextArea } = Input
const { Option } = Select

const UPDATE_TYPE_OPTIONS: { label: string; value: LeagueUpdateType }[] = [
  { label: 'Live Update', value: 'liveUpdate' },
  { label: 'Announcement', value: 'announcement' },
  { label: 'Reminder', value: 'reminder' },
  { label: 'Result', value: 'result' },
  { label: 'Other', value: 'other' },
]

interface UploadedFile {
  id: string
  url: string
  type: 'image' | 'video' | 'audio' | 'document'
  name?: string
}

interface AddUpdateFormProps {
  form: FormInstance
}

const AddUpdateForm: React.FC<AddUpdateFormProps> = ({ form }) => {
  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const leagueId = leagueData?._id || ''

  const { selectedUpdate, setSelectedUpdate, setAddUpdateModal, setEditUpdateModal } =
    leagueUpdatesStore(store => ({
      selectedUpdate: store.selectedUpdate,
      setSelectedUpdate: store.setSelectedUpdate,
      setAddUpdateModal: store.setAddUpdateModal,
      setEditUpdateModal: store.setEditUpdateModal,
    }))

  const setModalBtnLoader = myStore(store => store.setModalBtnLoader)
  const [sendNotification, setSendNotification] = useState(
    selectedUpdate?.sendNotification ?? false,
  )
  const [mapModalVisible, setMapModalVisible] = useState(false)
  const [locationData, setLocationData] = useState<any>(selectedUpdate?.address ?? null)
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(() => {
    if (!selectedUpdate) return []
    const imgs = (selectedUpdate.images || []).map(f => ({
      id: f._id,
      url: f.thumb || f.post || f.original || '',
      type: 'image' as const,
    }))
    const vids = (selectedUpdate.videos || []).map(f => ({
      id: f._id,
      url: f.link || f.original || '',
      type: 'video' as const,
    }))
    const auds = (selectedUpdate.audios || []).map(f => ({
      id: f._id,
      url: f.link || f.original || '',
      type: 'audio' as const,
    }))
    const docs = (selectedUpdate.documents || []).map(f => ({
      id: f._id,
      url: f.original || '',
      type: 'document' as const,
      name: f.name ? `${f.name}${f.ext ? `.${f.ext}` : ''}` : 'Document',
    }))
    return [...imgs, ...vids, ...auds, ...docs]
  })

  const { mutate: createUpdate } = useCreateLeagueUpdate()
  const { mutate: editUpdate }   = useEditLeagueUpdate()
  const { data: playersData }    = useGetLeaguePlayers(leagueId)
  const { mutate: uploadFile, isLoading: uploading } = useFileUploadRequest()
  const kids = playersData?.participants?.kids || []

  const makeUploadProps = (fileType: 'image' | 'video' | 'audio' | 'document'): UploadProps => ({
    showUploadList: false,
    accept:
      fileType === 'image' ? 'image/*' :
      fileType === 'video' ? 'video/*' :
      fileType === 'audio' ? 'audio/*' :
      '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv',
    multiple: true,
    beforeUpload: (file) => {
      const objectUrl = URL.createObjectURL(file)
      const formData = new FormData()
      formData.append('file', file)
      uploadFile(
        {
          payload: formData,
          newFile: {
            title: 'league-update',
            description: fileType,
            altText: fileType,
            sourceType: 'live_update',
          },
        },
        {
          onSuccess: (data: any) => {
            setUploadedFiles(prev => [
              ...prev,
              {
                id: data._id,
                url: objectUrl,
                type: fileType,
                name: fileType === 'document' ? file.name : undefined,
              },
            ])
          },
        },
      )
      return false
    },
  })

  const removeFile = (id: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== id))
  }

  const handleFinish = (values: any) => {
    const imageIds    = uploadedFiles.filter(f => f.type === 'image').map(f => f.id)
    const videoIds    = uploadedFiles.filter(f => f.type === 'video').map(f => f.id)
    const audioIds    = uploadedFiles.filter(f => f.type === 'audio').map(f => f.id)
    const documentIds = uploadedFiles.filter(f => f.type === 'document').map(f => f.id)

    const payload: LeagueUpdatePayloadType = {
      content: values.content,
      leagueId,
      title: values.title || undefined,
      type: values.type || undefined,
      kidIds: values.kidIds?.length ? values.kidIds : undefined,
      imageIds: imageIds.length ? imageIds : undefined,
      videoIds: videoIds.length ? videoIds : undefined,
      audioIds: audioIds.length ? audioIds : undefined,
      documentIds: documentIds.length ? documentIds : undefined,
      sendNotification,
      visibility: values.visibility as LeagueUpdateVisibility,
      address: values.address || undefined,
    }

    setModalBtnLoader(true)

    if (selectedUpdate) {
      editUpdate(
        {
          payload: {
            content: values.content,
            title: values.title || undefined,
            type: values.type,
            kidIds: values.kidIds,
            imageIds: imageIds.length ? imageIds : undefined,
            videoIds: videoIds.length ? videoIds : undefined,
            audioIds: audioIds.length ? audioIds : undefined,
            documentIds: documentIds.length ? documentIds : undefined,
            sendNotification,
            visibility: values.visibility,
            address: values.address || undefined,
          },
          updateId: selectedUpdate._id,
        },
        {
          onSuccess: () => {
            setModalBtnLoader(false)
            setSelectedUpdate(null)
            setEditUpdateModal(false)
          },
          onError: () => setModalBtnLoader(false),
        },
      )
    } else {
      createUpdate(payload, {
        onSuccess: () => {
          setModalBtnLoader(false)
          setAddUpdateModal(false)
        },
        onError: () => setModalBtnLoader(false),
      })
    }
  }

  const imageFiles    = uploadedFiles.filter(f => f.type === 'image')
  const videoFiles    = uploadedFiles.filter(f => f.type === 'video')
  const audioFiles    = uploadedFiles.filter(f => f.type === 'audio')
  const documentFiles = uploadedFiles.filter(f => f.type === 'document')

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        title: selectedUpdate?.title ?? '',
        content: selectedUpdate?.content ?? '',
        type: selectedUpdate?.type === 'news' ? 'liveUpdate' : selectedUpdate?.type,
        kidIds: selectedUpdate?.kidIds ?? [],
        visibility: selectedUpdate?.visibility ?? 'public',
        address: selectedUpdate?.address ?? undefined,
      }}
      onFinish={handleFinish}
    >
      <Form.Item name="address" hidden>
        <Input />
      </Form.Item>

      {/* ── Visibility ──────────────────────────────────────────────────── */}
      <p className="text-14 w-600 mb-8px">Who can see this update?</p>
      <Form.Item name="visibility" className="mb-20px">
        <Radio.Group>
          <div className="flex flex-column gap-8px">
            <Radio value="public">Public</Radio>
            <Radio value="parentWithRegisteredKids">Parents of registered players</Radio>
            <Radio value="parentWithTaggedKids">Parents of tagged players only</Radio>
          </div>
        </Radio.Group>
      </Form.Item>

      {/* ── Notification ────────────────────────────────────────────────── */}
      <div
        className="flex align-center justify-between mb-20px py-12px px-16px radius-12"
        style={{ border: '1px solid #EBEDF1', background: '#FAFAFA' }}
      >
        <div>
          <p className="text-14 w-600 mb-2px">Send push notification</p>
          <p className="text-12" style={{ color: '#908F98', margin: 0 }}>
            Notify the chosen audience on their device
          </p>
        </div>
        <Switch
          checked={sendNotification}
          onChange={setSendNotification}
          style={{ background: sendNotification ? '#E69741' : undefined }}
        />
      </div>

      {/* ── Title ───────────────────────────────────────────────────────── */}
      <Form.Item name="title" className="mb-16px">
        <Input className="br-12" placeholder="Title (optional)" />
      </Form.Item>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <p className="text-14 w-600 mb-8px">What do you want to say?</p>
      <Form.Item
        name="content"
        className="mb-20px"
        rules={[{ required: true, message: 'Content is required' }]}
      >
        <TextArea rows={4} className="br-12" placeholder="Write your update here..." />
      </Form.Item>

      {/* ── Type + Kids ─────────────────────────────────────────────────── */}
      <Row gutter={16} className="mb-20px">
        <Col span={12}>
          <label className="input-label">Type</label>
          <Form.Item name="type" className="mb-0">
            <Select placeholder="Select type" allowClear className="br-12">
              {UPDATE_TYPE_OPTIONS.map(({ label, value }) => (
                <Option key={value} value={value}>{label}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <label className="input-label">Tag Players</label>
          <Form.Item name="kidIds" className="mb-0">
            <Select mode="multiple" placeholder="Select players" allowClear className="br-12">
              {kids.map(kid => (
                <Option key={kid._id} value={kid._id}>
                  {`${kid.firstName} ${kid.lastName}`.trim()}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* ── Uploaded previews ───────────────────────────────────────────── */}
      {imageFiles.length > 0 && (
        <div className="flex gap-10px flex-wrap mb-16px">
          {imageFiles.map(f => (
            <div key={f.id} style={{ position: 'relative' }}>
              <img
                alt=""
                className="image-closing-icon"
                src={Icon}
                style={{ position: 'absolute', top: 4, right: 4, cursor: 'pointer', zIndex: 1 }}
                onClick={() => removeFile(f.id)}
              />
              <AntImage
                src={f.url}
                width={100}
                height={80}
                style={{ objectFit: 'cover', borderRadius: 8 }}
                preview={false}
              />
            </div>
          ))}
        </div>
      )}

      {videoFiles.length > 0 && (
        <div className="flex gap-10px flex-wrap mb-16px">
          {videoFiles.map(f => (
            <div key={f.id} style={{ position: 'relative', width: 100, height: 80 }}>
              <img
                alt=""
                className="image-closing-icon"
                src={Icon}
                style={{ position: 'absolute', top: 4, right: 4, cursor: 'pointer', zIndex: 1 }}
                onClick={() => removeFile(f.id)}
              />
              <video src={f.url} width={100} height={80} style={{ objectFit: 'cover', borderRadius: 8 }} />
            </div>
          ))}
        </div>
      )}

      {audioFiles.length > 0 && (
        <div className="flex flex-column gap-8px mb-16px">
          {audioFiles.map(f => (
            <div key={f.id} style={{ position: 'relative' }}>
              <img
                alt=""
                src={Icon}
                style={{ position: 'absolute', top: 4, right: 4, cursor: 'pointer', zIndex: 1 }}
                onClick={() => removeFile(f.id)}
              />
              <audio src={f.url} controls style={{ width: '100%' }} />
            </div>
          ))}
        </div>
      )}

      {documentFiles.length > 0 && (
        <div className="flex flex-column gap-8px mb-16px">
          {documentFiles.map(f => (
            <div
              key={f.id}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: '#F5F5F7',
                borderRadius: 8,
                border: '1px solid #EBEDF1',
              }}
            >
              <img src={DocumentIcon} style={{ width: 20, height: 20, flexShrink: 0 }} alt="doc" />
              <span className="text-13" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.name || 'Document'}
              </span>
              <img
                alt=""
                src={Icon}
                style={{ cursor: 'pointer', flexShrink: 0 }}
                onClick={() => removeFile(f.id)}
              />
            </div>
          ))}
        </div>
      )}

      {/* ── Media upload buttons ─────────────────────────────────────────── */}
      <div className="flex gap-16px align-center" style={{ borderTop: '1px solid #EBEDF1', paddingTop: 16 }}>
        <Upload {...makeUploadProps('image')}>
          <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: uploading ? 0.5 : 1 }}>
            <img alt="image" src={Gallery} style={{ width: 24, height: 24 }} />
          </button>
        </Upload>
        <Upload {...makeUploadProps('video')}>
          <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: uploading ? 0.5 : 1 }}>
            <img alt="video" src={PlayerImage} style={{ width: 24, height: 24 }} />
          </button>
        </Upload>
        <Upload {...makeUploadProps('audio')}>
          <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: uploading ? 0.5 : 1 }}>
            <img alt="audio" src={Music} style={{ width: 24, height: 24 }} />
          </button>
        </Upload>
        <Upload {...makeUploadProps('document')}>
          <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: uploading ? 0.5 : 1 }}>
            <img alt="document" src={DocumentIcon} style={{ width: 24, height: 24 }} />
          </button>
        </Upload>
        <Button
          icon={<EnvironmentOutlined />}
          className="media-button"
          htmlType="button"
          onClick={() => setMapModalVisible(true)}
        >
          Location
        </Button>
        {uploading && <span className="text-12" style={{ color: '#908F98' }}>Uploading...</span>}
      </div>

      {/* ── Location preview ─────────────────────────────────────────────── */}
      {locationData && (
        <div className="location-preview">
          <div className="location-preview-header">
            <EnvironmentOutlined />
            <span>
              {`${locationData.street || ''}, ${locationData.city || ''}, ${locationData.country || ''}`}
            </span>
            <button
              type="button"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={() => {
                setLocationData(null)
                form.setFieldsValue({ address: undefined })
              }}
            >
              <img src={Icon} alt="remove" style={{ width: 14, height: 14 }} />
            </button>
          </div>
          <div className="location-map-wrapper">
            <ViewAddress onlyView mapData={locationData} />
          </div>
        </div>
      )}

      {mapModalVisible && (
        <MapModal
          setMapModalVisible={setMapModalVisible}
          setMapModalData={(data: any) => {
            setLocationData(data)
            form.setFieldsValue({ address: data })
          }}
          mapData={locationData}
          setFieldsValue={(data: any) => form.setFieldsValue({ address: data })}
        />
      )}
    </Form>
  )
}

export default AddUpdateForm
